
import { StyleSearch } from "@/components/StyleSearch";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAdmin } from "@/providers/AdminProvider";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { LogOut } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export function Header({ showAboutLink = false }: { showAboutLink?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAdmin();
  const { toast } = useToast();

  const handleSearch = async (uuid: string) => {
    navigate(`/bag/${uuid}`);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your admin account",
      });
      
      navigate("/");
    } catch (error) {
      toast({
        title: "Error logging out",
        description: "There was a problem logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <header className="text-center space-y-4 mb-8 relative">
      {isAdmin && (
        <div className="absolute right-0 top-0">
          <Button 
            variant="outline"
            onClick={handleLogout}
            size="sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      )}
      
      <h1 className="text-4xl sm:text-5xl font-display font-medium">
        Vintage Coach Bag Database
      </h1>
      <p className="text-sm text-muted-foreground mt-2">
        Please note: The information in this database is accurate according to the available catalogs through 2002. <br />
        Your bag may have a different color, year, or strap length than what's listed, but that doesn't mean it is not authentic.
      </p>
      <StyleSearch onSearch={handleSearch} isLoading={false} />
      {showAboutLink && (
        <div className="mt-4 text-sm text-muted-foreground">
          <Dialog>
            <DialogTrigger className="hover:underline">
              About the Creator
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="mb-4">About the Creator</DialogTitle>
                <DialogDescription className="space-y-4 text-left">
                  <p>
                    I'm an Old Coach enthusiast looking to share resources in new, convenient ways. 
                    To further support me, you can subscribe to my YouTube{" "}
                    <a 
                      href="https://www.youtube.com/@OldCoachCleaning" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      @OldCoachCleaning
                    </a>{" "}
                    or check out the bags I have listed on{" "}
                    <a 
                      href="https://oldcoachcleaning.etsy.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      oldcoachcleaning.etsy.com
                    </a>
                  </p>
                  
                  <p>
                    Much of the data for this site was provided in a spreadsheet by Reddit user{" "}
                    <a 
                      href="https://www.reddit.com/user/abbythestabby/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      @abbythestabby
                    </a>
                    , whose contributions to{" "}
                    <a 
                      href="https://www.reddit.com/r/VintageCoachRehab/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      r/VintageCoachRehab
                    </a>{" "}
                    are unrivaled and appreciated by all Old Coach lovers.
                  </p>
                  
                  <p>
                    If you have questions or comments, please reach out to me via my YouTube or 
                    Etsy accounts.
                  </p>
                  
                  <p>Love, Old Coach Cleaning</p>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </header>
  );
}
